import React, { useState } from 'react';
import Select from 'react-select';
import './PetEntry.css';
import ImageUploader from '../../components/ImageUploader';
import useUploadImages from '../../hooks/useUploadImages';
import useCreatePet from '../../hooks/useCreatePet';
import useUpdatePet from '../../hooks/useUpdatePet';

const allFeaturesOptions = [
  { value: 'dobar sa djecom', label: 'Dobar sa djecom' },
  { value: 'dobar sa drugim psima', label: 'Dobar sa drugim psima' },
  { value: 'dobar sa macama', label: 'Dobar sa macama' },
];

const showAlert = (type, message, method, timeout = 10000) => {
  const container = document.getElementById('contact_results_' + method);
  if (!container) return;

  container.innerHTML = `
  <br/>
    <div class="alert alert-${type}" role="alert">
      ${message}
    </div>
  `;

  // Automatski ukloni alert nakon timeout milisekundi
  setTimeout(() => {
    container.innerHTML = '';
  }, timeout);
};

const PetEntry = () => {
  const [birthdate, setBirthdate] = useState('');
  const [features, setFeatures] = useState(allFeaturesOptions);
  const [colors, setColors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [images, setImages] = useState([]);
  const [resetKey, setResetKey] = useState(0);
  const [alert, setAlert] = useState(null);

  const createPetMutation = useCreatePet();
  const uploadImagesMutation = useUploadImages();
  const updatePetMutation = useUpdatePet();

  const handleUpload = newFiles => {
    setImages(prev => {
      const merged = [...prev, ...newFiles];

      // dedupe po name+size kombinaciji
      const unique = Array.from(
        new Map(merged.map(file => [file.name + file.size, file])).values()
      );

      console.log('📁 Unique images:', unique);
      return unique;
    });
  };

  const handleRemoveImage = removedFile => {
    setImages(prev => prev.filter(img => img !== removedFile));
  };

  const handleSubmit = async e => {
    e.preventDefault(); // blokira default submit, ali browser i dalje provjerava required

    const form = e.target;

    // Ako forma nije validna, browser će automatski pokazati greške
    if (!form.checkValidity()) return;
    setFieldErrors({});
    // 1. Prikupljanje fieldova
    const name = form.nameSelect.value.trim();
    const petTypeRaw = form.speciesSelect.value;
    const genderRaw = form.genderSelect.value;
    const vaccinatedRaw = form.vaccinatedSelect.value;
    const neuteredRaw = form.sterilizedSelect.value;
    const sizeRaw = form.sizeSelect.value;
    const statusRaw = form.statusSelect.value;

    const about = form.about.value.trim();
    const about_en = form.about_en.value.trim();
    const description = form.description.value.trim();
    const description_en = form.description_en.value.trim();
    const adminPassword = form.adminPassword.value.trim();

    const sponsorsRaw = form.sponsors.value
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    // 2. Mapiranje vrijednosti na backend šemu
    const pet_type =
      petTypeRaw === 'pas' ? 'dog' : petTypeRaw === 'mačka' ? 'cat' : 'other';
    const gender = genderRaw === 'ženski' ? 'female' : 'male';
    const vaccinated = vaccinatedRaw === 'Da';
    const neutered = neuteredRaw === 'Da';
    const size_category =
      sizeRaw === 'mali' ? 'small' : sizeRaw === 'srednji' ? 'medium' : 'large';
    const status =
      statusRaw === 'slobodan'
        ? 'available'
        : statusRaw === 'rezervisan'
          ? 'reserved'
          : statusRaw === 'fosterDK'
            ? 'fosterDK'
          : statusRaw === 'fosterCZ'
            ? 'fosterCZ'
          : 'adopted';

    // features → boolean flags
    const good_with_kids = features.some(f => f.value.includes('djec'));
    const good_with_dogs = features.some(f => f.value.includes('ps'));
    const good_with_cats = features.some(f => f.value.includes('mac'));

    const colorsList = colors.map(c => c.value);

    // sponsors → array of objects
    const sponsors = sponsorsRaw.map(s => {
      const allowedTypes = ['gold', 'silver', 'bronze'];
      const [name, type] = s.split(':');
      const types = type?.trim().toLowerCase();
      if (!allowedTypes.includes(types)) {
        setFieldErrors(prev => ({
          ...prev,
          sponsors: `Nepoznat tip sponzora "${type}" za "${name.trim()}". Dozvoljeni tipovi su: gold, silver i bronze`,
        }));
        throw new Error(
          `Invalid sponsor type "${type}" for sponsor "${name.trim()}". Allowed types: ${allowedTypes.join(', ')}`
        );
      }
      return {
        name: name.trim(),
        sponsor_type: type.trim(), // gold/silver/bronze
      };
    });

    // 3. Finalni payload
    const payload = {
      name,
      birth_date: new Date(birthdate),
      pet_type,
      gender,
      status,
      colors: colorsList,
      size_category,
      neutered,
      vaccinated,
      about,
      about_en,
      description,
      description_en,
      good_with_kids,
      good_with_dogs,
      good_with_cats,
      image_main: '',
      images: [],
      sponsors,
      adminPassword
    };

    // Sending data to backend API
    try {
      const result = await createPetMutation.mutateAsync(payload);
      const petId = result.id;
      // alert('Ljubimac uspješno unesen!');
      showAlert('success', 'Ljubimac uspješno unesen!', 'create');

      const folder = `adoption/${payload.name}-${petId}`;
      console.log('folder', folder);

      // Upload of images to the folder
      let uploadedImages = { urls: [] };
      if (images.length > 0) {
        try {
          uploadedImages = await uploadImagesMutation.mutateAsync({
            files: images,
            folder,
          });
          console.log('Uploaded images:', uploadedImages);
          showAlert('success', 'Slike uspješno unesene!', 'upload');
        } catch (uploadErr) {
          console.error(uploadErr);
          showAlert('danger', 'Greška pri uploadu slika!', 'upload');
        }
      }

      try {
        const updatedPayload = {
          image_main: uploadedImages.urls[0] || '',
          images: uploadedImages.urls,
          adminPassword,
        };

        console.log('updating pet', updatedPayload);
        const uploadResult = await updatePetMutation.mutateAsync({
          id: petId,
          data: updatedPayload,
        });
        console.log('uploadResult', uploadResult);
        showAlert('success', 'Uspješno dodavanje slika ljubimcu!', 'update');
        // alert('Psu uspješno dodane slike!');
      } catch (updateErr) {
        console.error(updateErr);
        showAlert('danger', 'Greška pri dodavanju slika ljubimcu!', 'update');
      }

      // RESET
      form.reset();
      setFeatures(allFeaturesOptions);
      setColors([]);
      setBirthdate('');
      setResetKey(prev => prev + 1);
      setImages([]);
    } catch (err) {
      console.error(err);
      showAlert('danger', 'Greška pri unosu ljubimca!', 'create');
    }
  };

  return (
    <div>
      <div className="col-lg-12">
        <div className="paws-house-bg1 bg-light-custom element-section">
          <div className="contact-info col-lg-8">
            <h4>Unos ljubimca</h4>
            <div id="contact_form">
              <form className="form-group" onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <label htmlFor="nameSelect">
                      Ime ljubimca<span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="nameSelect"
                      name="nameSelect"
                      className="form-control input-field"
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="speciesSelect">
                        Vrsta ljubimca<span className="required">*</span>
                      </label>
                      <select className="form-control" id="speciesSelect">
                        <option>pas</option>
                        <option>mačka</option>
                      </select>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="genderSelect">
                        Spol<span className="required">*</span>
                      </label>
                      <select className="form-control" id="genderSelect">
                        <option>ženski</option>
                        <option>muški</option>
                      </select>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="form-group">
                      <label htmlFor="vaccinatedSelect">
                        Vakcinisan/a<span className="required">*</span>
                      </label>
                      <select className="form-control" id="vaccinatedSelect">
                        <option>Da</option>
                        <option>Ne</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label htmlFor="sterilizedSelect">
                        Sterilisan/a<span className="required">*</span>
                      </label>
                      <select className="form-control" id="sterilizedSelect">
                        <option>Da</option>
                        <option>Ne</option>
                      </select>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="birthdateSelect">
                        Datum rođenja<span className="required">*</span>
                      </label>
                      <input
                        type="date"
                        id="birthdateSelect"
                        name="birthdate"
                        className="form-control input-field"
                        value={birthdate}
                        onChange={e => setBirthdate(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label>
                      Veličina<span className="required">*</span>
                    </label>
                    <select className="form-control" id="sizeSelect">
                      <option>srednji</option>
                      <option>mali</option>
                      <option>veliki</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label>
                      Status<span className="required">*</span>
                    </label>
                    <select className="form-control" id="statusSelect">
                      <option>slobodan</option>
                      <option>rezervisan</option>
                      <option>fosterDK</option>
                      <option>fosterCZ</option>
                      <option>usvojen</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="featuresSelect">Osobine</label>
                    <Select
                      options={allFeaturesOptions}
                      isMulti
                      id="featuresSelect"
                      value={features}
                      onChange={setFeatures}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      placeholder="Odaberi osobine..."
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="colorSelect">
                      Boje<span className="required">*</span>
                    </label>
                    <Select
                      options={[
                        { value: 'white', label: 'bijeli' },
                        { value: 'black', label: 'crni' },
                        { value: 'brown', label: 'smedji' },
                        { value: 'grey', label: 'sivi' },
                        { value: 'beige', label: 'bež' },
                        { value: 'ginger', label: 'narandzasta' },
                      ]}
                      isMulti
                      id="colorSelect"
                      value={colors}
                      onChange={setColors}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      placeholder="Odaberi boje..."
                      required
                    />
                  </div>

                  <div className="col-md-12">
                    <label>
                      Kratki opis<span className="required">*</span>
                    </label>
                    <textarea
                      type="text"
                      name="about"
                      rows="2"
                      className="form-control textarea-field"
                      style={{
                        borderRadius: 0,
                      }}
                      required
                    />
                  </div>

                  <div className="col-md-12">
                    <label>
                      Kratki opis na engleskom
                      <span className="required">*</span>
                    </label>
                    <textarea
                      type="text"
                      name="about_en"
                      rows="2"
                      className="textarea-field form-control"
                      style={{
                        borderRadius: 0,
                      }}
                      required
                    />
                  </div>

                  <div className="col-md-12">
                    <label>
                      Opis<span className="required">*</span>
                    </label>
                    <textarea
                      name="description"
                      id="description"
                      className="textarea-field form-control"
                      rows="4"
                      style={{
                        borderRadius: 0,
                      }}
                      required
                    ></textarea>
                  </div>

                  <div className="col-md-12">
                    <label>
                      Opis na engleskom<span className="required">*</span>
                    </label>
                    <textarea
                      name="description_en"
                      id="description_en"
                      className="textarea-field form-control"
                      rows="4"
                      style={{
                        borderRadius: 0,
                      }}
                      required
                    ></textarea>
                  </div>

                  <div className="col-md-12">
                    <label>
                      Sponzori - razdvojiti zarezom, <b>format ime:tip</b> npr:
                      ime1:gold, ime2: silver, ime3:bronze
                    </label>
                    <textarea
                      name="sponsors"
                      id="sponsors"
                      className="textarea-field form-control"
                      style={{
                        borderRadius: 0,
                      }}
                      rows="3"
                    ></textarea>
                    {fieldErrors.sponsors && (
                      <div className="text-danger">{fieldErrors.sponsors}</div>
                    )}
                  </div>

                  <div className="col-md-12">
                    <div style={{ margin: '50px auto' }}>
                      <ImageUploader
                        onUpload={handleUpload}
                        onRemove={handleRemoveImage}
                        resetKey={resetKey}
                      />
                    </div>
                  </div>
                </div>
                {/* Hidden Username to satisfy Chrome Password Manager heuristics */}
                <input
                  type="text"
                  name="username"
                  value="admin"
                  autoComplete="username"
                  style={{ display: 'none' }}
                  readOnly
                />

                <div className="col-md-6">
                  <label htmlFor="adminPassword">
                    Admin Password<span className="required">*</span>
                  </label>
                  <input
                    type="password"
                    id="adminPassword"
                    name="adminPassword"
                    className="form-control input-field"
                    autoComplete="current-password"
                    required
                  />
                </div>  
                <button
                  type="submit"
                  id="submit_btn"
                  value="Submit"
                  className="btn btn-primary"
                >
                  Unesi
                </button>
              </form>
              <div id="contact_results_create"></div>
              <div id="contact_results_upload"></div>
              <div id="contact_results_update"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PetEntry;
